import React, { useState } from "react";
import { useTable, List, EditButton, ShowButton, CreateButton } from "@refinedev/antd";
import { Table, Space, Tag, Input } from "antd";
import dayjs from "dayjs";
import { SearchOutlined } from "@ant-design/icons";
import { useList } from "@refinedev/core";
import { PURCHASE_ORDER_QUERY } from "./queries";
import { TEAM_LIST_QUERY } from "../teams/queries";
import { PurchaseOrder, PurchaseOrderStatus } from "./interface";

interface IPurchaseOrderRow extends PurchaseOrder {}

const StatusBadge: React.FC<{ status: PurchaseOrderStatus }> = ({ status }) => {
  const colorMap: Record<PurchaseOrderStatus, string> = {
    [PurchaseOrderStatus.DRAFT]: "default",
    [PurchaseOrderStatus.SUBMITTED]: "processing",
    [PurchaseOrderStatus.VALIDATED]: "purple",
    [PurchaseOrderStatus.PARTIALLY_APPROVED]: "warning",
    [PurchaseOrderStatus.APPROVED]: "success",
    [PurchaseOrderStatus.REJECTED]: "error",
    [PurchaseOrderStatus.SENT_TO_VENDOR]: "blue",
    [PurchaseOrderStatus.ACKNOWLEDGED]: "cyan",
    [PurchaseOrderStatus.IN_PROGRESS]: "gold",
    [PurchaseOrderStatus.PARTIALLY_DELIVERED]: "lime",
    [PurchaseOrderStatus.DELIVERED]: "green",
    [PurchaseOrderStatus.COMPLETED]: "geekblue",
    [PurchaseOrderStatus.CANCELLED]: "red",
  };
  return <Tag color={colorMap[status] || "default"}>{status}</Tag>;
};

export const PurchaseOrderList: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");

  // Teams for mapping teamId -> team name
  const { data: teamsData, isLoading: teamsLoading, isError: teamsError } = useList<any>({
    resource: "teams",
    meta: { gqlQuery: TEAM_LIST_QUERY },
  });
  const teams =
    Array.isArray(teamsData?.data)
      ? teamsData?.data
      : (teamsData?.data as any)?.teams?.nodes || [];

  const { tableProps } = useTable<IPurchaseOrderRow>({
    resource: "purchaseOrders",
    initialSorter: [{ field: "createdAt", order: "asc" }],
    initialPageSize: 10,
    meta: {
      gqlQuery: PURCHASE_ORDER_QUERY,
    },
    onSearch: (params: any) => {
      const { q } = params;
      return [
        {
          field: "orderNo",
          operator: "contains",
          value: q ? q : undefined,
        },
        {
          field: "vendor.companyName",
          operator: "contains",
          value: q ? q : undefined,
        },
      ];
    },
  });

  const dataSource: IPurchaseOrderRow[] = ((tableProps as any)?.dataSource || []).filter(
    (row: IPurchaseOrderRow) =>
      !searchTerm ||
      row.orderNo?.toLowerCase?.().includes(searchTerm.toLowerCase()) ||
      row.vendor?.companyName?.toLowerCase?.().includes(searchTerm.toLowerCase())
  );

  return (
    <List
      headerProps={{
        extra: (
          <Space>
            <Input
              placeholder="Search by PO No or Vendor..."
              prefix={<SearchOutlined />}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onPressEnter={(e) => setSearchTerm(e.currentTarget.value)}
              style={{ width: 240 }}
            />
            <CreateButton />
          </Space>
        ),
      }}
    >
      <Table {...tableProps} dataSource={dataSource} rowKey="id">
        <Table.Column dataIndex="orderNo" title="PO No" sorter />
        <Table.Column
          dataIndex={["vendor", "companyName"]}
          title="Vendor"
          sorter
        />
        <Table.Column
          dataIndex="teamId"
          title="Team"
          sorter
          render={(teamId: string) => {
            if (teamsLoading) return "Loading...";
            if (teamsError) return "Error";
            const team = (teams as any[]).find((t) => t.id === teamId);
            return team ? team.name : "Unknown";
          }}
        />
        <Table.Column dataIndex="dateCreated" title="Date Created" render={(date: string) => dayjs(date).format("YYYY-MM-DD")} sorter />
        <Table.Column
          dataIndex="totalAmount"
          title="Total Amount"
          sorter
          render={(v: number, record: IPurchaseOrderRow) => `${record.currency || "ETB"} ${Number(v ?? 0).toLocaleString()}`}
        />
        <Table.Column
          dataIndex="status"
          title="Status"
          render={(status: PurchaseOrderStatus) => <StatusBadge status={status} />}
          sorter
        />
        <Table.Column
          title="Actions"
          dataIndex="actions"
          render={(_text: string, record: IPurchaseOrderRow) => (
            <Space>
              <EditButton size="small" recordItemId={record.id} resource="purchaseOrders" />
              <ShowButton size="small" recordItemId={record.id} resource="purchaseOrders" />
            </Space>
          )}
        />
      </Table>
    </List>
  );
};